import React from 'react';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { useVocabulary } from '../context/VocabularyContext';
import { srsManager } from '../utils/srsManager';

export function ResultsScreen() {
  const { state, dispatch, actions } = useVocabulary();
  const { sessionConfig, selectedCategory, selectedStage } = state;
  const { type, words, sentences, poolCode } = sessionConfig;

  const handleBackToPools = () => {
    // Return to the pool list based on exercise type
    if (type === 'vocabulary') {
      dispatch({ type: actions.SET_CURRENT_VIEW, payload: 'word-pools' });
    } else if (type === 'sentences') {
      dispatch({ type: actions.SET_CURRENT_VIEW, payload: 'sentence-subcategories' });
    }
    dispatch({ type: actions.SET_APP_STATE, payload: 'menu' });
  };

  const handleRestartSession = () => {
    // Restart the same pool
    if (type === 'vocabulary' && poolCode) {
      dispatch({ 
        type: actions.START_WORD_SESSION, 
        payload: { 
          categoryKey: selectedCategory,
          poolCode: poolCode,
          words: words,
          mode: 'flashcards'
        } 
      });
    } else if (type === 'sentences' && poolCode) {
      dispatch({ 
        type: actions.START_SENTENCE_SESSION, 
        payload: { 
          stage: selectedStage, 
          poolCode: poolCode,
          sentences: sentences,
          mode: 'flashcards'
        } 
      });
    }
  };

  // Calculate statistics
  const items = type === 'vocabulary' ? words : sentences;
  const totalItems = items?.length || 0;
  
  // Get SRS stats for the pool (or aggregate for random sessions)
  const category = type === 'vocabulary' ? 'words' : 'sentences';
  let srsStats = null;
  
  if (poolCode) {
    // Regular session - single pool
    srsStats = srsManager.getPoolStats(category, poolCode, totalItems);
  } else if (items && items.length > 0) {
    // Random session - aggregate stats from all pools
    const poolGroups = {};
    items.forEach(item => {
      if (item._poolCode) {
        if (!poolGroups[item._poolCode]) {
          poolGroups[item._poolCode] = [];
        }
        poolGroups[item._poolCode].push(item);
      }
    });
    
    // Aggregate stats from all pools
    srsStats = {
      total: 0,
      dueForReview: 0,
      tomorrow: 0,
      mastered: 0
    };
    
    Object.entries(poolGroups).forEach(([code, poolItems]) => {
      const poolStats = srsManager.getPoolStats(category, code, poolItems.length);
      srsStats.total += poolStats.total;
      srsStats.dueForReview += poolStats.dueForReview;
      srsStats.tomorrow += poolStats.tomorrow;
      srsStats.mastered += poolStats.mastered;
    });
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBackToPools}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          title="Powrót do listy pul"
        >
          <ArrowLeft size={20} />
        </button>
        
        <h1 className="text-2xl font-bold text-gray-800">
          Podsumowanie sesji
        </h1>
        
        <div className="w-10"></div> {/* Spacer for layout balance */}
      </div>

      {/* Main Result Card */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
        {/* SRS Progress */}
        {srsStats && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
              📊 Postęp w puli
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-orange-50 rounded-xl border border-orange-200">
                <div className="text-2xl font-bold text-orange-600 mb-1">
                  {srsStats.dueForReview}
                </div>
                <div className="text-xs text-gray-600">Do powtórki</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {srsStats.tomorrow}
                </div>
                <div className="text-xs text-gray-600">Na jutro</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {srsStats.mastered}
                </div>
                <div className="text-xs text-gray-600">Opanowane</div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className={`grid ${srsStats && srsStats.dueForReview > 0 && poolCode ? 'grid-cols-2' : 'grid-cols-1'} gap-4 mt-8`}>
          {srsStats && srsStats.dueForReview > 0 && poolCode && (
            <button
              onClick={handleRestartSession}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-all"
            >
              <RotateCcw size={20} />
              Powtórz pulę
            </button>
          )}
          <button
            onClick={handleBackToPools}
            className="flex items-center justify-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-6 rounded-lg transition-all"
          >
            <ArrowLeft size={20} />
            Lista pul
          </button>
        </div>
      </div>

    </div>
  );
} 